import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import {
  getCurrentUser,
  logout as logoutClient,
  initiateLogin,
} from "@/lib/idgov";
import type { AuthContextState, IdGovUser } from "@/lib/idgov";
import { directus } from "@/lib/directus";
import { readItems } from "@directus/sdk";

// Dev mode configuration - ONLY enabled via build-time environment variable
// This ensures dev mode cannot be enabled in production
const DEV_MODE = import.meta.env.VITE_DEV_MODE === "true";

// Dev user credentials (from environment - for real Directus auth in dev mode)
const DEV_USER_EMAIL = import.meta.env.VITE_DEV_USER_EMAIL || "";
const DEV_USER_PASSWORD = import.meta.env.VITE_DEV_USER_PASSWORD || "";

// Cleanup old dev mode localStorage keys (migration from old implementation)
const OLD_DEV_MODE_KEYS = [
  "svitlodim_dev_mode",
  "svitlodim_dev_enabled",
  "dev_mode_enabled",
  "devMode",
];

function cleanupOldDevModeCache() {
  let cleaned = false;
  OLD_DEV_MODE_KEYS.forEach((key) => {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      cleaned = true;
    }
  });
  if (cleaned) {
    console.log("[Auth] Cleaned up old dev mode localStorage cache");
  }
}

interface AuthContextValue extends AuthContextState {
  login: (redirectPath?: string) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isDevMode: boolean;
  disableDevMode: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthContextState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const refreshUser = useCallback(async () => {
    // In dev mode, authenticate with Directus and load REAL dev user
    if (DEV_MODE) {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      
      try {
        // Authenticate with Directus using dev credentials
        if (DEV_USER_EMAIL && DEV_USER_PASSWORD) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (directus as any).login(DEV_USER_EMAIL, DEV_USER_PASSWORD);
          console.log("Dev mode: Authenticated with Directus");
        } else {
          console.warn("Dev mode: Missing VITE_DEV_USER_EMAIL or VITE_DEV_USER_PASSWORD");
        }
        
        // Load REAL dev user from database by directus_user_id
        // This queries the current authenticated user's idgov_users record
        const users = await directus.request(
          readItems("idgov_users", {
            filter: { directus_user_id: { _eq: "$CURRENT_USER" } },
            limit: 1,
          })
        ) as IdGovUser[];
        
        const devUser = users[0];
        if (!devUser) {
          throw new Error("Dev user record not found in idgov_users");
        }
        
        console.log("Dev mode: Loaded real user from Directus:", devUser);
        
        setState({
          user: devUser,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error("Dev mode: Failed to load dev user:", error);
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: "Failed to load dev user",
        });
      }
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    const { user, error } = await getCurrentUser();

    setState({
      user,
      isAuthenticated: !!user,
      isLoading: false,
      error: error ?? null,
    });
  }, []);

  useEffect(() => {
    // Clean up old dev mode localStorage keys on app start
    cleanupOldDevModeCache();
    refreshUser();
  }, [refreshUser]);

  const login = useCallback((redirectPath?: string) => {
    initiateLogin(redirectPath);
  }, []);

  const logout = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));
    await logoutClient();
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const disableDevMode = useCallback(() => {
    // Dev mode is controlled by build-time env var, cannot be disabled at runtime
    // This is kept for backwards compatibility but does nothing
    console.warn("Dev mode is controlled by VITE_DEV_MODE environment variable");
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshUser, isDevMode: DEV_MODE, disableDevMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

export type { AuthContextValue };
